import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { clientApi } from '@/config/axios';
import { isPublicPath } from './publicPaths';
import { clearViewerBinding } from './viewerSession';
import {
  type ProfessionalSignupBody,
  signupProfessional as signupProfessionalRequest,
  type SignupResponse,
} from './signup-api';

export type Edition = 'professional' | 'personal';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithTokens: (tokens: { access_token: string; refresh_token: string }) => Promise<void>;
  logout: () => void;
  organization: Organization | null;
  organizations: Organization[];
  refreshUser: () => Promise<void>;
  setOrganization: (org: Organization) => void;
  signupProfessional: (body: ProfessionalSignupBody) => Promise<void>;
  user: AuthUser | null;
}

interface AuthUser {
  email: string;
  id: string;
  is_superuser: boolean;
  name: string;
}

interface Organization {
  edition: Edition;
  id: string;
  is_active: boolean;
  is_owner: boolean;
  organization_id: string;
  organization_name: string | null;
  role_id: string;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const ORG_KEY = 'selected_org';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [organization, setOrganizationState] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const setOrganization = useCallback((org: Organization) => {
    setOrganizationState(org);
    localStorage.setItem(ORG_KEY, JSON.stringify(org));
  }, []);

  const logout = useCallback(() => {
    clearViewerBinding();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ORG_KEY);
    setUser(null);
    setOrganizationState(null);
    window.location.href = '/login';
  }, []);

  const fetchUser = useCallback(async () => {
    // Patient links authenticate themselves with a scoped token in the URL.
    // Calling /auth/me here with a leftover staff token would 401, and the 401
    // handler navigates to /login, discarding the link the patient arrived on.
    if (isPublicPath()) {
      setIsLoading(false);
      return;
    }
    const token = getAccessToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await clientApi.get('/api/v1/auth/me');
      setUser(response.data);

      // Fetch orgs list
      const orgsResponse = await clientApi.get('/api/v1/auth/me/organizations');
      setOrganizations(orgsResponse.data);

      // Load saved org or use first
      const savedOrg = localStorage.getItem(ORG_KEY);
      let parsed: Organization | null = null;
      if (savedOrg) {
        try {
          parsed = JSON.parse(savedOrg) as Organization;
        } catch {
          localStorage.removeItem(ORG_KEY);
        }
      }
      if (parsed) {
        // Refresh from the server so name / edition / role stay in sync with
        // what the backend currently believes. We write back through
        // setOrganization (not setOrganizationState) so the localStorage
        // snapshot — read by route guards before AuthContext mounts —
        // never drifts from the canonical org shape.
        const fresh = orgsResponse.data.find((o: Organization) => o.organization_id === parsed.organization_id);
        if (fresh) {
          setOrganization(fresh);
        } else {
          setOrganizationState(parsed);
        }
      } else if (orgsResponse.data.length > 0) {
        setOrganization(orgsResponse.data[0]);
      }
    } catch {
      logout();
    } finally {
      setIsLoading(false);
    }
  }, [logout, setOrganization]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  /**
   * Re-read the signed-in user after they edit their own profile.
   *
   * Only /auth/me, not the full `fetchUser` — memberships and the selected
   * organization are untouched by a profile edit, and re-running that path
   * would also re-run its failure branch, which logs the user out.
   *
   * The PATCH response already carries the updated user, but this deliberately
   * re-reads instead: /auth/me is the one shape the app boots from, so there is
   * no second code path that can drift from it. One extra GET on a rare action
   * is a fair price for that.
   */
  const refreshUser = useCallback(async () => {
    const response = await clientApi.get('/api/v1/auth/me');
    setUser(response.data);
  }, []);

  /**
   * Everything that follows an issued token pair: store it, then load the
   * profile and organizations.
   *
   * Split out of `login` so social sign-in — which gets its tokens from
   * /auth/social/{provider} rather than /auth/login — lands in the same state
   * through the same code, instead of a second copy that can drift.
   */
  const loginWithTokens = useCallback(
    async ({ access_token, refresh_token }: { access_token: string; refresh_token: string }) => {
      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);

      // Fetch user profile and orgs
      const meResponse = await clientApi.get('/api/v1/auth/me');
      setUser(meResponse.data);

      const orgsResponse = await clientApi.get('/api/v1/auth/me/organizations');
      setOrganizations(orgsResponse.data);
      if (orgsResponse.data.length > 0) {
        setOrganization(orgsResponse.data[0]);
      }
    },
    [setOrganization],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await clientApi.post('/api/v1/auth/login', { email, password });
      await loginWithTokens(response.data);
    },
    [loginWithTokens],
  );

  const seedFromSignup = useCallback(
    async (response: SignupResponse) => {
      // Stage tokens so the next axios call carries Authorization, but only
      // promote them to "logged in" state once the membership fetch succeeds.
      // On failure we roll back to a fully signed-out state instead of
      // leaving the user with valid tokens and no organization.
      localStorage.setItem(TOKEN_KEY, response.access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token);
      try {
        const orgsResponse = await clientApi.get('/api/v1/auth/me/organizations');
        setUser({
          id: response.user.id,
          name: response.user.name,
          email: response.user.email,
          is_superuser: response.user.is_superuser,
        });
        setOrganizations(orgsResponse.data);
        if (orgsResponse.data.length > 0) {
          setOrganization(orgsResponse.data[0]);
        }
      } catch (err) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(ORG_KEY);
        setUser(null);
        setOrganizationState(null);
        setOrganizations([]);
        throw err;
      }
    },
    [setOrganization],
  );

  const signupProfessional = useCallback(
    async (body: ProfessionalSignupBody) => {
      const response = await signupProfessionalRequest(body);
      await seedFromSignup(response);
    },
    [seedFromSignup],
  );

  const isAuthenticated = !!user;

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      isLoading,
      refreshUser,
      organization,
      organizations,
      loginWithTokens,
      isAuthenticated,
      setOrganization,
      signupProfessional,
    }),
    [
      user,
      refreshUser,
      isAuthenticated,
      isLoading,
      login,
      loginWithTokens,
      logout,
      organization,
      organizations,
      setOrganization,
      signupProfessional,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
